<template>
  <div>
    <draggable
      v-model="pictures"
      draggable=".item"
      style="display: flex; flex-wrap: wrap"
      @start="drag = true"
      @end="
        drag = false;
        onDragend();
      "
    >
      <div
        v-for="m in pictures"
        :key="m.id"
        class="item"
      >
        <div
          style="
            width: 200px;
            background: #eee;
            border-radius: 5px;
            position: relative;
            overflow: hidden;
            margin-right: 10px;
            margin-bottom: 10px;
          "
        >
          <button
            type="button"
            class="delete"
            style="position: absolute; top: 4px; right: 4px; z-index: 1"
            @click="deleteMedia(m)"
          />
          <b-image
            :src="m.mime_type && m.mime_type.startsWith('video/') ? m.thumb_url : m.url"
            :alt="m.filename"
            ratio="6by4"
          />
          <b-icon
            v-if="m.mime_type && m.mime_type.startsWith('video/')"
            icon="play-circle"
            size="is-medium"
            style="position: absolute; bottom: 4px; left: 4px; color: #fff; text-shadow: 0 1px 4px rgba(0,0,0,.6)"
          />
          <button
            v-if="m.mime_type && m.mime_type.startsWith('video/')"
            type="button"
            class="button is-small"
            style="position: absolute; bottom: 4px; right: 4px; z-index: 1"
            :title="$t('choose_video_preview')"
            @click="openPosterPicker(m)"
          >
            <b-icon
              icon="image-edit"
              size="is-small"
            />
          </button>
        </div>
      </div>
    </draggable>

    <!-- Video poster picker: seek to a frame, use it as the preview -->
    <b-modal
      v-model="posterPickerOpen"
      :width="720"
    >
      <div
        v-if="posterMedia"
        class="box"
      >
        <p class="title is-6 mb-3">
          {{ $t("choose_video_preview") }}
        </p>
        <video
          ref="posterVideo"
          :src="posterMedia.url"
          controls
          preload="metadata"
          style="width: 100%; border-radius: 6px; background: #000"
        />
        <p class="is-size-7 has-text-grey mt-2">
          {{ $t("choose_video_preview_hint") }}
        </p>
        <div class="mt-3 has-text-right">
          <b-button @click="posterPickerOpen = false">
            {{ $t("cancel").capitalize() }}
          </b-button>
          <b-button
            type="is-primary"
            :loading="posterSaving"
            @click="savePoster"
          >
            {{ $t("use_this_frame") }}
          </b-button>
        </div>
      </div>
    </b-modal>

    <b-field class="mb-2">
      <b-upload
        :value="uploads"
        :loading="uploading"
        drag-drop
        multiple
        accept="image/*,video/mp4,video/quicktime"
        @input="onUpload"
      >
        <section class="section">
          <div class="content has-text-centered">
            <p>
              <b-icon
                icon="upload"
                size="is-large"
              />
            </p>
            <p>{{ $t("drop_or_click_to_upload") }}</p>
          </div>
        </section>
      </b-upload>
    </b-field>

    <!-- Upload feedback: byte progress, then an indeterminate "processing"
         phase while the server transcodes videos / stores files -->
    <div
      v-if="uploading"
      class="mb-3"
    >
      <b-progress
        :value="isProcessingPhase ? undefined : uploadPercent"
        type="is-dark"
        size="is-small"
      />
      <p class="is-size-7 has-text-grey mt-1">
        {{ isProcessingPhase
          ? $t("upload_processing")
          : $t("upload_progress", { percent: uploadPercent }) }}
      </p>
    </div>
    <div class="py-2 mb-2">
      <b-message v-if="mode == 'create'">
        {{ $t("click_next_notification") }}
      </b-message>
    </div>
  </div>
</template>

<script>
import draggable from "vuedraggable";
import { resizeImage, convertHeicImage } from "@/services/helpers";
export default {
  components: {
    draggable,
  },
  props: {
    property: {
      required: true,
      type: Object,
    },
    resize: {
      default: 1980,
      type: Number,
    },
    mode: {
      default: "update",
      type: String,
    },
  },
  data() {
    return {
      uploads: [],
      drag: false,
      deleting: false,
      uploading: false,
      posterPickerOpen: false,
      posterMedia: null,
      posterSaving: false,
      uploadProgress: {},
      pictures: this.property.media,
      imageConversionMimeTypes:[
        "image/heic",
        "image/heif",
      ]
    };
  },
  computed: {
    uploadPercent() {
      const items = Object.values(this.uploadProgress);

      if (!items.length) {
        return 0;
      }
      const total = items.reduce((sum, i) => sum + i.total, 0);
      const loaded = items.reduce((sum, i) => sum + Math.min(i.loaded, i.total), 0);

      return Math.min(100, Math.round((loaded / total) * 100));
    },
    // All bytes sent but requests still pending = the server is working
    // (video transcode can take a while)
    isProcessingPhase() {
      const items = Object.values(this.uploadProgress);

      return items.length > 0 && items.every((i) => i.loaded >= i.total);
    },
  },
  watch: {
    property: {
      handler() {
        this.pictures = this.property.media;
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    async onUpload(files) {

      if (this.uploading) {
        return;
      }

      this.uploading = true;
      this.uploadProgress = {};

      Promise.all(files.map((file, index) => this.uploadFile(file, index)))
        .then(() => {
          this.onUploadSuccess();
        })
        .finally(() => {
          this.uploads = [];
          this.uploading = false;
          this.uploadProgress = {};
        });
    },
    async uploadFile(file, index) {

      const isVideo = (file.type || "").startsWith("video/");

      if (!isVideo && this.imageConversionMimeTypes.includes(file.type)) {
        file = await convertHeicImage(file);
      }

      // Videos are uploaded as-is (resizeImage is canvas/image-only and
      // would hang forever on a video file)
      if (!isVideo) {
        file = await resizeImage(file, this.resize);
      }

      const formData = new FormData();

      formData.append("file", file);

      this.$set(this.uploadProgress, index, { loaded: 0, total: file.size || 1 });

      await this.$http.post(
        `/api/properties/${this.property.id}/media`,
        formData,
        {
          onUploadProgress: (e) => {
            this.$set(this.uploadProgress, index, {
              loaded: e.loaded,
              total: e.total || file.size || 1,
            });
          },
        }
      );
    },
    openPosterPicker(media) {
      this.posterMedia = media;
      this.posterPickerOpen = true;
    },
    savePoster() {
      const video = this.$refs.posterVideo;

      if (!video || this.posterSaving) {
        return;
      }
      video.pause();
      this.posterSaving = true;
      this.$http
        .post(
          `/api/properties/${this.property.id}/media/${this.posterMedia.id}/poster`,
          { time: video.currentTime || 0 }
        )
        .then(() => {
          this.posterPickerOpen = false;
          // Refetch the property so every thumb_url picks up the new poster
          this.$emit("upload");
        })
        .finally(() => {
          this.posterSaving = false;
        });
    },
    deleteMedia(media) {
      if (this.deleting) {
        return;
      }
      this.deleting = true;
      this.$http
        .delete(`/api/properties/${this.property.id}/media/${media.id}`)
        .then(() => {
          this.onDelete();
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    onDragend() {
      this.$http.patch(`/api/properties/${this.property.id}/media/reorder`, {
        ids: this.pictures.map((m) => m.id),
      });
    },
    onUploadSuccess() {
      this.$emit("upload");
    },
    onDelete() {
      this.$emit("delete");
    },
  },
};
</script>
